import * as React from 'react';
import { FC } from 'react';
import { useRecordContext } from 'react-admin';
import { BoxedShowLayout } from 'ra-compact-ui';
import { Typography, Card, CardContent, Box } from '@material-ui/core';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import {
    CheckCircle,
    RadioButtonUnchecked,
    LocalShipping,
    Payment,
    Cancel,
    PlayArrow,
    GetApp,
    Send,
    Error,
    ThumbUp,
} from '@material-ui/icons';
import { useTranslate } from 'react-admin';

const API_URL = process.env.REACT_APP_REST_API_URL;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        showLayout: {
            padding: '16px',
        },
        timelineContainer: {
            maxWidth: '800px',
        },
        timelineItem: {
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: '24px',
            position: 'relative',
            '&:not(:last-child)::before': {
                content: '""',
                position: 'absolute',
                left: '20px',
                top: '48px',
                height: 'calc(100% + 8px)',
                width: '2px',
                backgroundColor: '#e0e0e0',
                zIndex: 1,
            },
        },
        iconContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            marginRight: '16px',
            zIndex: 2,
            position: 'relative',
            flexShrink: 0,
        },
        successIcon: {
            backgroundColor: '#4caf50',
        },
        warningIcon: {
            backgroundColor: '#ff9800',
        },
        errorIcon: {
            backgroundColor: '#f44336',
            color: 'white',
        },
        infoIcon: {
            backgroundColor: '#2196f3',
        },
        contentContainer: {
            flex: 1,
            marginBottom: '8px',
        },
        statusCard: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: '8px',
        },
        statusTitle: {
            fontWeight: 600,
            marginBottom: '4px',
            textTransform: 'capitalize',
        },
        statusDate: {
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            marginBottom: '8px',
        },
        statusDuration: {
            display: 'inline-block',
            marginLeft: '8px',
        },
        additionalData: {
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
        },
        statusImage: {
            maxWidth: '200px',
            maxHeight: '200px',
            borderRadius: '4px',
            marginTop: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        comment: {
            fontStyle: 'italic',
            color: theme.palette.text.secondary,
        },
    })
);

// Status configuration with icons and colors
const statusConfig: {
    [key: string]: { icon: any; className: string };
} = {
    new: {
        icon: RadioButtonUnchecked,
        className: 'infoIcon',
    },
    accepted: {
        icon: ThumbUp,
        className: 'successIcon',
    },
    canceled: {
        icon: Cancel,
        className: 'errorIcon',
    },
    conceled: {
        // Note: matches your data structure typo
        icon: Cancel,
        className: 'errorIcon',
    },
    engaged: {
        icon: PlayArrow,
        className: 'successIcon',
    },
    started: {
        icon: LocalShipping,
        className: 'successIcon',
    },
    loaded: {
        icon: GetApp,
        className: 'successIcon',
    },
    shipped: {
        icon: Send,
        className: 'successIcon',
    },
    shipping_confirmed: {
        icon: CheckCircle,
        className: 'successIcon',
    },
    shipping_confirmed_auto: {
        icon: CheckCircle,
        className: 'successIcon',
    },
    shipping_not_confirmed: {
        icon: Error,
        className: 'errorIcon',
    },
    payed: {
        icon: Payment,
        className: 'successIcon',
    },
    not_payed: {
        icon: Payment,
        className: 'warningIcon',
    },
};

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
        date: date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }),
        time: date.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
        }),
    };
};

// const formatDuration = (duration: number) => {
//     if (duration === 0) return null;
//     if (duration < 60) return `${duration}s`;
//     if (duration < 3600) return `${Math.floor(duration / 60)}min`;
//     return `${Math.floor(duration / 3600)}h${Math.floor(
//         (duration % 3600) / 60
//     )}min`;
// };

const CourseHistory: FC = () => {
    const classes = useStyles();
    const translate = useTranslate();
    const record = useRecordContext();

    if (!record || !record.statut_info) {
        return null;
    }

    // Filter and sort status items that have dates
    const statusItems = Object.entries(record.statut_info)
        .filter(([_, statusData]: [string, any]) => statusData.date)
        .map(([statusKey, statusData]: [string, any]) => ({
            key: statusKey,
            ...statusData,
            timestamp: new Date(statusData.date).getTime(),
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

    return (
        <BoxedShowLayout className={classes.showLayout}>
            <Box className={classes.timelineContainer}>
                <Typography
                    variant="h5"
                    gutterBottom
                    style={{ marginBottom: '32px' }}
                >
                    {translate('resources.courses.history.title')}
                </Typography>

                {statusItems.map((item, index) => {
                    const config = statusConfig[item.key] || {
                        icon: RadioButtonUnchecked,
                        className: 'infoIcon',
                    };
                    const IconComponent = config.icon;
                    const { date, time } = formatDate(item.date);
                    // const duration = formatDuration(item.duration);

                    // Get translated status label
                    const statusLabel = translate(
                        `resources.courses.history.statuses.${item.key}`,
                        { _: item.key }
                    );

                    return (
                        <div
                            key={`${item.key}-${index}`}
                            className={classes.timelineItem}
                        >
                            <div
                                className={`${classes.iconContainer} ${
                                    classes[config.className]
                                }`}
                            >
                                <IconComponent
                                    style={{
                                        color: 'white',
                                    }}
                                />
                            </div>

                            <div className={classes.contentContainer}>
                                <Card className={classes.statusCard}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            className={classes.statusTitle}
                                        >
                                            {statusLabel}
                                        </Typography>

                                        <Typography
                                            className={classes.statusDate}
                                        >
                                            {date}{' '}
                                            {translate(
                                                'resources.courses.history.labels.at'
                                            )}{' '}
                                            {time}
                                            {/* {duration && (
                                                <Chip
                                                    size="small"
                                                    label={duration}
                                                    variant="outlined"
                                                    className={
                                                        classes.statusDuration
                                                    }
                                                />
                                            )} */}
                                        </Typography>

                                        {item.data && (
                                            <div
                                                className={
                                                    classes.additionalData
                                                }
                                            >
                                                {item.data.comment && (
                                                    <>
                                                        <Typography variant="body2">
                                                            {translate(
                                                                'resources.courses.history.labels.comment'
                                                            )}
                                                            :
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.comment
                                                            }
                                                            style={{
                                                                marginBottom:
                                                                    '8px',
                                                            }}
                                                        >
                                                            {item.data.comment}
                                                        </Typography>
                                                    </>
                                                )}

                                                {item.data.image && (
                                                    <div>
                                                        <Typography
                                                            variant="body2"
                                                        >
                                                            {translate(
                                                                'resources.courses.history.labels.photo'
                                                            )}
                                                            :
                                                        </Typography>
                                                        <img
                                                            src={`${API_URL}/files/${item.data.image}/view`}
                                                            alt={`${translate(
                                                                'resources.courses.history.labels.photo'
                                                            )} ${statusLabel}`}
                                                            className={
                                                                classes.statusImage
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    );
                })}
            </Box>
        </BoxedShowLayout>
    );
};

export default CourseHistory;
